import { Constants } from '../internal/Constants';
import { GetCurrentLanguage, GetOriginalLanguage, Localized, LocalizedOriginal, SetTranslatedLocalizationObject } from '../internal/Localized';
import { GetLocaleData, GetLocaleList, Logger, SetDisplayLocale } from '@shared/common';
import { GetUserLanguage, IsBenignNetworkError, IsBotUserAgent, LobbyClient, Telemetry } from '@shared/frontend';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import type { LocalizationCode } from '@shared/common';

/** File-local constants for LocaleProvider. */
const localConstants = {
  logContext: 'LocaleProvider',
  settingLanguage: 'language',
  abortErrorName: 'AbortError',
  initialLangVersion: 0,
  langVersionStep: 1,
  initialGeneration: 0,
  generationStep: 1,
  dirRTL: 'rtl',
  dirLTR: 'ltr'
} as const;

/** Locale action types. */
enum LocaleActionType {
  LanguageChanged,
  TranslationDone,
  TranslationError
}

/** Locale state shape. */
interface ILocaleState {
  langLoaded: boolean;
  selectedLang: LocalizationCode;
  langError: unknown | null;
  // Increments every time the localization dictionary is (re)applied. Components memoizing label-derived
  // values depend on this so they recompute when the active language changes.
  langVersion: number;
}

/** A selectable language option for the language selector. */
export interface ILanguageOption {
  code: LocalizationCode;
  label: string;
}

/** Locale context shape exposed to consumers. */
interface ILocaleContext extends ILocaleState {
  selectLang: (code: LocalizationCode) => void;
  languages: ILanguageOption[];
  isRTL: boolean;
}

/** Locale action union. */
type LocaleAction = { type: LocaleActionType.LanguageChanged; lang: LocalizationCode } | { type: LocaleActionType.TranslationDone } | { type: LocaleActionType.TranslationError; error: unknown };

/**
 * Reducer for locale state.
 *
 * @param state - Current state.
 * @param action - Action to apply.
 * @returns The next state.
 */
function localeReducer(state: ILocaleState, action: LocaleAction): ILocaleState {
  switch (action.type) {
    case LocaleActionType.LanguageChanged: {
      return { ...state, selectedLang: action.lang, langLoaded: false, langError: null };
    }
    case LocaleActionType.TranslationDone: {
      return { ...state, langLoaded: true, langError: null, langVersion: state.langVersion + localConstants.langVersionStep };
    }
    case LocaleActionType.TranslationError: {
      // A failed translation must not strand the app on the full-screen loader (App gates render on langLoaded) or freeze the language selector (it shows a spinner while !langLoaded). Release the
      // gate and revert the selection to the language actually applied, so the UI keeps the last good dictionary and re-picking the failed language retries the request.
      return { ...state, selectedLang: GetCurrentLanguage(), langLoaded: true, langError: action.error };
    }
    default: {
      return state;
    }
  }
}

/** Initial locale state. */
const initialState: ILocaleState = {
  langLoaded: false,
  selectedLang: GetUserLanguage(),
  langError: null,
  langVersion: localConstants.initialLangVersion
};

/** Locale context instance. */
const LocaleContext = createContext<ILocaleContext | undefined>(undefined);

/**
 * Provider that owns the active language and keeps the localization dictionary translated to it.
 *
 * On language change it translates {@link LocalizedOriginal} via the lobby translate endpoint (reverting to the untranslated source directly when the original language is selected), then bumps
 * `langVersion` so label-derived memoized values recompute.
 *
 * @param props - Component props.
 * @param props.children - Wrapped children.
 * @returns The provider element.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(localeReducer, initialState);

  // Tracks the in-flight translate request so a newer language selection can supersede (abort) the
  // previous one. The effect cleanup deliberately does NOT abort: navigating away or unmounting mid-flight
  // must let the request finish so its result still lands in the IndexedDB/global cache for next time.
  const inFlightRef = useRef<AbortController | null>(null);
  // Monotonic token identifying the latest translate request. A superseded request that still resolves
  // must not apply its stale dictionary over the now-current language.
  const generationRef = useRef<number>(localConstants.initialGeneration);

  // Translate (or revert) the localization dictionary whenever the selected language changes.
  useEffect(() => {
    if (state.selectedLang === GetCurrentLanguage()) {
      dispatch({ type: LocaleActionType.TranslationDone });
      return;
    }
    if (state.selectedLang === GetOriginalLanguage()) {
      // Reverting to the source language needs no network round-trip — restore the original strings in place.
      SetTranslatedLocalizationObject({ source: GetOriginalLanguage(), target: GetOriginalLanguage(), content: { ...LocalizedOriginal } });
      dispatch({ type: LocaleActionType.TranslationDone });
      return;
    }
    // A fresh language selection supersedes the previous in-flight request; abort it so we do not pile up
    // concurrent translations. Navigation/unmount does NOT reach here, so those requests are left running.
    inFlightRef.current?.abort();
    const controller = new AbortController();
    inFlightRef.current = controller;
    const generation = (generationRef.current += localConstants.generationStep);
    LobbyClient.Translate(controller.signal, GetOriginalLanguage(), state.selectedLang, LocalizedOriginal, Constants.ContextAI)
      .then((locData) => {
        // Only the most recent request may apply its result; a superseded language must not clobber the current one.
        if (generation !== generationRef.current) return;
        SetTranslatedLocalizationObject(locData);
        dispatch({ type: LocaleActionType.TranslationDone });
      })
      .catch((err: unknown) => {
        if (Logger.GetErrorName(err) !== localConstants.abortErrorName) {
          // Crawler-aborted or offline network failures are expected noise; record them as logs, not errors.
          if (IsBotUserAgent() || IsBenignNetworkError(err)) Logger.Log(localConstants.logContext, Logger.GetErrorMessage(err));
          else Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
          dispatch({ type: LocaleActionType.TranslationError, error: err });
        }
      });
    // Intentionally no cleanup that aborts: let in-flight translations finish (and cache) even when the
    // user navigates away before the request completes.
  }, [state.selectedLang]);

  const selectLang = useCallback(
    (code: LocalizationCode) => {
      // Selecting the already-active language is a no-op: there is nothing to re-translate. Dispatching would flip langLoaded false, but the [selectedLang]-keyed effect — whose dependency value is
      // unchanged — never re-runs to flip it back, stranding the selector on a permanent spinner. Skip entirely so the spinner never shows when the language does not actually change.
      if (code === state.selectedLang) return;
      dispatch({ type: LocaleActionType.LanguageChanged, lang: code });
      Telemetry.SettingsChanged(localConstants.settingLanguage);
    },
    [state.selectedLang]
  );

  // Selectable languages with their display label resolved from the active dictionary. Recomputed on every
  // language change so the labels themselves follow the active language.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const languages = useMemo<ILanguageOption[]>(() => GetLocaleList().map((locale) => ({ code: locale.Code, label: Localized[locale.Code] ?? locale.Code })), [state.langVersion]);

  const isRTL = useMemo(() => GetLocaleData(state.selectedLang).RTL, [state.selectedLang]);

  // Reflect the active language and its writing direction on the document element so RTL languages (Arabic,
  // Hebrew, Persian, Urdu) flip the whole layout via CSS logical properties, and assistive tech reads the
  // correct language. This is the single place that owns both `isRTL` and the selected code.
  useEffect(() => {
    document.documentElement.dir = isRTL ? localConstants.dirRTL : localConstants.dirLTR;
    document.documentElement.lang = state.selectedLang;
    // Keep date/time formatting (FormatTimestamp) aligned with the in-app language choice rather than the browser locale.
    SetDisplayLocale(state.selectedLang);
  }, [isRTL, state.selectedLang]);

  const value = useMemo<ILocaleContext>(() => ({ ...state, selectLang, languages, isRTL }), [state, selectLang, languages, isRTL]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/**
 * @returns The locale context.
 * @throws Error when used outside a LocaleProvider.
 */
export function useLocale(): ILocaleContext {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within a LocaleProvider');
  return ctx;
}
